class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.isHeaderSearch = !!this.closest('.header-search-dropdown');
    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (!this.searchRecommendationEmpty) {
      this.searchContent?.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (!this.isHeaderSearch) {
      document.body.classList.add('predictive-search-open');
    }
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent?.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (this.contains(event.target)) return;
    if (this.isHeaderSearch) return;
    setTimeout(() => {
      document.body.classList.remove('predictive-search-open');
      this.classList.remove('predictive-search-open');
    });
  }
}
customElements.define('predictive-search', PredictiveSearch);

class HeaderSearch extends HTMLElement {
  constructor() {
    super();
    this.dropdown = document.getElementById(this.dataset.triggerId);
    this.overlay = this.querySelector('.header-search-overlay');
    this.triggers = Array.from(document.querySelectorAll(`[aria-controls="${this.dataset.triggerId}"]`));
    this._isOpen = false;

    this.triggers.forEach(t => t.addEventListener('click', this.toggle.bind(this)));
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this));
    }
    const closeBtn = this.dropdown && this.dropdown.querySelector('.header-search__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.close.bind(this));
    }
    document.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('click', this.onClickOutside.bind(this));
    if (this.dropdown) {
      this.dropdown.addEventListener('click', this.onKeywordClick.bind(this));
    }
  }

  toggle() {
    this._isOpen ? this.close() : this.open();
  }

  open() {
    this._isOpen = true;
    if (this.dropdown) {
      this.dropdown.classList.add('is-open');
      this.dropdown.setAttribute('aria-hidden', 'false');
    }
    if (this.overlay) this.overlay.classList.add('is-open');
    this.triggers.forEach(t => t.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('header-search-open');
    requestAnimationFrame(() => {
      const input = this.dropdown && this.dropdown.querySelector('input[type="search"]');
      if (input) input.focus();
    });
  }

  close() {
    this._isOpen = false;
    if (this.dropdown) {
      this.dropdown.classList.remove('is-open');
      this.dropdown.setAttribute('aria-hidden', 'true');
      const ps = this.dropdown.querySelector('predictive-search');
      if (ps) {
        ps.removeAttribute('results');
        const psInput = ps.querySelector('input[type="search"]');
        if (psInput) psInput.value = '';
      }
    }
    if (this.overlay) this.overlay.classList.remove('is-open');
    this.triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('header-search-open');
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this._isOpen) {
      this.close();
      const visibleTrigger = this.triggers.find(t => t.offsetParent !== null);
      if (visibleTrigger) visibleTrigger.focus();
    }
  }

  onClickOutside(event) {
    if (!this._isOpen) return;
    if (this.triggers.some(t => t.contains(event.target))) return;
    if (this.dropdown && this.dropdown.contains(event.target)) return;
    this.close();
  }

  onKeywordClick(event) {
    const link = event.target.closest('.header-search__keyword-btn');
    if (!link) return;
    event.preventDefault();
    const input = this.dropdown.querySelector('input[type="search"]');
    if (!input) return;
    input.value = link.dataset.term;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  }
}
customElements.define('header-search', HeaderSearch);

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

class JoinMidiCta extends HTMLElement {
  connectedCallback() {
    this._keywords = (this.getAttribute('keywords') || '')
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean);

    if (!this._keywords.length) return;

    const scope = this.closest('form') || this.closest('.shopify-section') || document;
    this._input = scope.querySelector('input[type="search"]');
    if (!this._input) return;

    this._handler = FoxTheme.utils.debounce(this._check.bind(this), 200);
    this._input.addEventListener('input', this._handler);
    this._check();
  }

  disconnectedCallback() {
    if (this._input && this._handler) {
      this._input.removeEventListener('input', this._handler);
    }
    clearTimeout(this._timer);
  }

  _matches(value) {
    const q = value.trim().toLowerCase();
    if (!q) return false;
    const padded = ' ' + q + ' ';
    return this._keywords.some((k) => q === k || padded.includes(' ' + k + ' '));
  }

  _check() {
    if (this._matches(this._input.value)) {
      this.removeAttribute('hidden');
    } else {
      this.setAttribute('hidden', '');
    }
  }
}
customElements.define('join-midi-cta', JoinMidiCta);

class SearchPageLiveResults {
  constructor() {
    const searchPage = document.querySelector('.template-search');
    if (!searchPage) return;

    const input = searchPage.querySelector('input[name="q"][type="search"]');
    if (!input) return;

    this.input = input;
    this.sectionId = FoxTheme.utils.getSectionId(input);
    this.facetForm = searchPage.querySelector('form[is="facet-form"]');

    input.addEventListener('input', FoxTheme.utils.debounce(this.onInput.bind(this), 800));

    // type="reset" restores inputs to their initial HTML attribute value (the
    // search term), not empty — override it via event delegation so it survives
    // DOM re-renders triggered by live results.
    searchPage.addEventListener('click', (e) => {
      if (!e.target.closest('.search__reset')) return;
      e.preventDefault();
      input.value = '';
      input.focus();
    });
  }

  buildUrl(q) {
    const url = new URL(window.location.href);
    // A new typed query resets filters — drop all filter.* params
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.')) url.searchParams.delete(key);
    }
    url.searchParams.set('q', q);
    url.searchParams.delete('page');
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  onInput() {
    const q = this.input.value.trim();
    if (q.length < 2) return;

    if (this.facetForm) {
      const hiddenQ = this.facetForm.querySelector('input[name="q"]');
      if (hiddenQ) hiddenQ.value = q;
    }

    fetch(this.buildUrl(q))
      .then((r) => r.text())
      .then((html) => this.render(html, q));
  }

  render(html, q) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const productGrid = document.getElementById('ProductGridContainer');
    const newProductGrid = doc.getElementById('ProductGridContainer');
    if (productGrid && newProductGrid) productGrid.innerHTML = newProductGrid.innerHTML;

    const productCount = document.getElementById('ProductCount');
    const newProductCount = doc.getElementById('ProductCount');
    if (productCount && newProductCount) productCount.innerHTML = newProductCount.innerHTML;

    const sortBy = document.getElementById('SortByContainer');
    const newSortBy = doc.getElementById('SortByContainer');
    if (sortBy && newSortBy) sortBy.innerHTML = newSortBy.innerHTML;

    const filtersActive = document.getElementById('FacetFiltersActive');
    const newFiltersActive = doc.getElementById('FacetFiltersActive');
    if (filtersActive && newFiltersActive) filtersActive.innerHTML = newFiltersActive.innerHTML;

    // Full swap so counts/ranges update and the container appears even when the
    // initial page had zero results
    const newFiltersContainer = doc.getElementById('FacetFiltersContainer');
    const filtersContainer = document.getElementById('FacetFiltersContainer');
    const productGridWrapper = document.getElementById('ProductGridWrapper');
    if (newFiltersContainer) {
      if (filtersContainer) {
        filtersContainer.replaceWith(newFiltersContainer.cloneNode(true));
      } else if (productGridWrapper) {
        productGridWrapper.insertAdjacentHTML('beforebegin', newFiltersContainer.outerHTML);
      }
    } else if (filtersContainer) {
      filtersContainer.remove();
    }

    // Update browser URL
    const historyUrl = new URL(window.location.href);
    historyUrl.searchParams.set('q', q);
    historyUrl.searchParams.delete('page');
    history.replaceState({}, '', historyUrl.toString());
  }
}

document.addEventListener('DOMContentLoaded', () => new SearchPageLiveResults());
